import React, { createContext, useContext, useCallback, useMemo, useState } from 'react';
import { useThreadManagement } from '../hooks/useThreadManagement';
import { useSidebar } from './SidebarContext';

/**
 * ThreadContext - Elevates thread management to app level
 *
 * This context wraps useThreadManagement hook and provides thread state
 * to both Sidebar (thread list) and Chat (current thread).
 *
 * Why elevate?
 * - Sidebar needs access to threads list for navigation
 * - Chat needs access to currentThread for messages
 * - Prevents prop drilling between AppShell → Sidebar → Chat
 */

const ThreadContext = createContext(null);

export const ThreadProvider = ({ children }) => {
  const { isMobile, closeOverlay } = useSidebar();

  // Pending rolId for new agent chats — state so welcome screen reacts
  const [pendingRolId, setPendingRolIdState] = useState(null);
  // Optional snapshot of the role that started this chat (name/color/objectives).
  // Chat.jsx normally resolves the agent from useRoles(), but that hook has no
  // shared cache: arriving from /explore remounts Chat with an empty roles array,
  // so the welcome screen flashed the generic brand header until the refetch
  // landed. The hint lets it paint the agent on the first frame. Purely a render
  // shortcut — useRoles stays the source of truth once loaded.
  const [pendingRoleHint, setPendingRoleHintState] = useState(null);

  // Use the existing thread management hook
  const threadManagement = useThreadManagement();

  const {
    threads,
    setThreads,
    currentThread,
    setCurrentThread,
    isThreadListOpen,
    setIsThreadListOpen,
    selectThread: baseSelectThread,
    clearTargetMessage,
    deleteThread,
    renameThread,
    applyThreadTitle,
    createThread,
    loadThreads,
    loadMoreThreads,
    hasMore,
    isLoadingMore,
    isLoading,
    searchTerm,
    setSearchTerm,
    isSearching,
    agentFilter,
    setAgentFilter,
  } = threadManagement;

  // Enhanced selectThread that also closes mobile overlay
  // Clear pendingRolId so the thread's own metadata.rolId takes precedence in Chat.jsx
  // #212: forwards an optional { targetMessageId } for plan-execution deep-links.
  const selectThread = useCallback((thread, options) => {
    setPendingRolIdState(null);
    setPendingRoleHintState(null);
    baseSelectThread(thread, options);
    if (isMobile) {
      closeOverlay();
    }
  }, [baseSelectThread, isMobile, closeOverlay]);

  // Set pending rolId for next thread creation (called by sidebar agent click).
  // `roleHint` is optional: callers that already hold the role object pass it so
  // the welcome screen renders without waiting for useRoles(). Ignored unless it
  // matches rolId, so a stale object can never mislabel the agent.
  const setPendingRolId = useCallback((rolId, roleHint = null) => {
    setPendingRolIdState(rolId);
    setPendingRoleHintState(rolId && roleHint?.id === rolId ? roleHint : null);
  }, []);

  // Consume pending rolId (read + clear, called during thread creation)
  const consumePendingRolId = useCallback(() => {
    const val = pendingRolId;
    setPendingRolIdState(null);
    setPendingRoleHintState(null);
    return val;
  }, [pendingRolId]);

  // Create new thread and close mobile overlay
  const handleNewThread = useCallback(() => {
    setCurrentThread(null);
    setIsThreadListOpen(false);
    if (isMobile) {
      closeOverlay();
    }
  }, [setCurrentThread, setIsThreadListOpen, isMobile, closeOverlay]);

  // Handle thread title updates from WebSocket (LLM-generated titles).
  // Delega en applyThreadTitle porque el título tiene que llegar a TODAS las
  // cachés de hilos, no solo a `threads`: al chatear con un experto la sidebar
  // muestra `agentFilterResults` (useStartAgentChat activa el filtro de agente),
  // y actualizar solo `threads` dejaba el hilo como "Nuevo Chat" hasta recargar
  // la página (#501).
  const handleThreadTitleUpdated = useCallback((threadId, newTitle) => {
    console.log('[ThreadContext] 📝 Updating thread title:', { threadId, newTitle });
    applyThreadTitle(threadId, newTitle);
  }, [applyThreadTitle]);

  // ✅ FIX: Wrap value in useMemo to prevent unnecessary re-renders of consumers
  const value = useMemo(() => ({
    // Thread list state
    threads,
    setThreads,
    isLoading,

    // Current thread state
    currentThread,
    setCurrentThread,

    // Thread list UI state (kept for compatibility)
    isThreadListOpen,
    setIsThreadListOpen,

    // Actions
    selectThread,
    clearTargetMessage,
    deleteThread,
    renameThread,
    createThread,
    handleNewThread,
    loadThreads,
    loadMoreThreads,
    handleThreadTitleUpdated,

    // Agent routing
    pendingRolId,
    pendingRoleHint,
    setPendingRolId,
    consumePendingRolId,

    // Pagination
    hasMore,
    isLoadingMore,

    // Search
    searchTerm,
    setSearchTerm,
    isSearching,

    // Agent filter
    agentFilter,
    setAgentFilter,
  }), [
    threads,
    setThreads,
    isLoading,
    currentThread,
    setCurrentThread,
    isThreadListOpen,
    setIsThreadListOpen,
    selectThread,
    clearTargetMessage,
    deleteThread,
    renameThread,
    createThread,
    handleNewThread,
    loadThreads,
    loadMoreThreads,
    handleThreadTitleUpdated,
    pendingRolId,
    pendingRoleHint,
    setPendingRolId,
    consumePendingRolId,
    hasMore,
    isLoadingMore,
    searchTerm,
    setSearchTerm,
    isSearching,
    agentFilter,
    setAgentFilter,
  ]);

  return (
    <ThreadContext.Provider value={value}>
      {children}
    </ThreadContext.Provider>
  );
};

// Hook to consume thread context
export const useThread = () => {
  const context = useContext(ThreadContext);
  if (context === null) {
    throw new Error('useThread must be used within a ThreadProvider');
  }
  return context;
};

export default ThreadContext;
